// https://github.com/sulu/sulu-demo/pull/78/files

// @flow
import React, { useEffect, Fragment } from "react";
import { autorun, computed } from "mobx";
import Moment from "moment";
import { observer } from "mobx-react";
import { Route } from "sulu-admin-bundle/services/Router";
import type { ViewProps } from "sulu-admin-bundle/containers/ViewRenderer";
import type { Element, Node } from "react";
import styles from "./headerStyle.scss";
import overrides from "./../../../css/customization.scss";
import Location from "../../../svgs/Location";
import Time from "../../../svgs/Time";
import Tag from "../../../svgs/Tag";
import Umpire from "../../../svgs/Umpire";
import { ResourceRequester } from "sulu-admin-bundle/services";

const getUmpires = async (umpireObject, locale) => {
    try {
        const umpireIds = Object.keys(umpireObject);
        const commaSeparatedIds = umpireIds.join(",");

        const response = await ResourceRequester.get("contacts", {
            ids: commaSeparatedIds,
            locale: locale,
            flat: true,
        });
        const umpireData = response["_embedded"].contacts;

        if (umpireData.length === 0) {
            console.log("No umpires found for provided IDs:", umpireIds);
            return [];
        }
        return umpireData;
    } catch (error) {
        console.error("Error fetching umpire data:", error);
    }
};

type Props<T> = {
    ...ViewProps,
    children?: (T) => Element<*> | null,
    childrenProps: T,
    header?: Node,
    routeChildren?: Array<Route>,
    selectedIndex?: number,
    title?: string,
};

@observer
class HeaderField<T> extends React.Component<Props<T>> {
    static defaultProps = {
        childrenProps: {},
    };

    redirectToRouteWithHighestPriorityDisposer: () => void;

    constructor(props: Props<T>) {
        super(props);

        this.state = {
            leagueName: [],
        };
    }

    componentDidMount() {
        let href = window.location.href;
        let needle =
            href.indexOf("dhbmatch/") > 0 ? "dhbmatch/" : "dhbmatches/";
        let index = href.indexOf(needle);
        let temp = href.slice(index + needle.length + 3);
        let index2 = temp.indexOf("/");
        let id = "";

        //check for case when there is no '/details' in URL, then there is no need for temp.substr(0, index2)
        if (index2 < 0) {
            id = temp;
        } else {
            id = temp.slice(0, index2);
        }

        const locale = "de";

        let baseUrl = location.protocol + "//" + location.host;
        let endPointData =
            baseUrl + `/admin/api/dhbmatches/${id}?locale=${locale}`;

        const loadData = async () => {
            try {
                const requestForthumbId = await fetch(endPointData);
                const data = await requestForthumbId.json();

                const logoHomeId = data?.homeTeam?.logo?.id;
                const logoId = data?.awayTeam?.logo?.id;
                let requestForthumb,
                    resultrequestThumb,
                    requestForHomethumb,
                    resultrequestHomeThumb = null;

                const umpireObject = data?.umpiresObject;
                const umpires = await getUmpires(umpireObject, locale);

                if (logoId) {
                    let endMediaPoint =
                        baseUrl + `/admin/api/media/${logoId}?locale=${locale}`;

                    requestForthumb = await fetch(endMediaPoint);
                    resultrequestThumb = await requestForthumb.json();
                }

                if (logoHomeId) {
                    let endMediaPointHome =
                        baseUrl +
                        `/admin/api/media/${logoHomeId}?locale=${locale}`;

                    requestForHomethumb = await fetch(endMediaPointHome);
                    resultrequestHomeThumb = await requestForHomethumb.json();
                }

                this.setState({
                    leagueName: data.leagueName,
                    leagueType: data.leagueType,
                    homeTeam: data.homeTeamName,
                    awayTeam: data.awayTeamName,
                    awayTeamLogo:
                        resultrequestThumb?.thumbnails?.["image-150.webp"],
                    homeTeamLogo:
                        resultrequestHomeThumb?.thumbnails?.["image-150.webp"],
                    matchDate: Moment(data.scheduleDate).format(
                        "D[.] MMMM YYYY"
                    ),
                    matchTime: data.scheduleTime,
                    matchLocation: data.locationName,
                    status: data.status,
                    gameNum: data.id,
                    awayHTGoals: data.awayHTGoals,
                    homeHTGoals: data.homeHTGoals,
                    awayGoals: data.awayGoals,
                    homeGoals: data.homeGoals,
                    umpires: umpires,
                    shootout: data.shootout,
                });
            } catch (error) {
                console.log(error);
            }
        };

        loadData();
    }

    render() {
        const {
            children,
            childrenProps,
            header,
            router,
            route,
            selectedIndex,
            title,
            isRootView,
        } = this.props;

        const childComponent = children ? children(childrenProps) : null;

        let added = 0;
        let upcoming = 1;
        let warmup = 2;
        let inprogress = 3;
        let ended = 4;
        let completed = 5;
        let official = 6;
        let progressResultMatch;
        let resultMatch;
        let matchTime;

        const currentStatus = this.state.status;

        if (currentStatus === added) {
            added = "activeMatch";
        } else if (currentStatus === upcoming) {
            upcoming = "activeMatch";
        } else if (currentStatus === warmup) {
            warmup = "active";
            added = upcoming = "passed";
        } else if (currentStatus === inprogress) {
            matchTime = "hide";
            inprogress = progressResultMatch = "active";
            added = warmup = upcoming = "passed";
        } else if (currentStatus === ended) {
            matchTime = "hide";
            ended = resultMatch = "active";
            added = warmup = upcoming = inprogress = "passed";
        } else if (currentStatus === completed) {
            matchTime = "hide";
            completed = resultMatch = "active";
            ended = added = warmup = upcoming = inprogress = "passed";
        } else if (currentStatus === official) {
            matchTime = "hide";
            official = resultMatch = "active";
            completed =
                ended =
                added =
                warmup =
                upcoming =
                inprogress =
                    "passed";
        }

        return (
            <Fragment>
                <div className={styles.headerTop}>
                    <p>Spielbericht</p>
                    <div style={{ display: "flex", padding: "0 15px" }}>
                        <p id={styles[added]} className={styles.offlineMatch}>
                            (0) IN VORBEREITUNG{" "}
                        </p>
                        <p
                            id={styles[upcoming]}
                            className={styles.offlineMatch}
                        >
                            (0) IN VORBEREITUNG
                        </p>
                        <span
                            id={styles[warmup]}
                            className={styles.matchStatus}
                        >
                            <p> 1 </p>
                            <p className={styles.NameStatus}>VOR DEM SPIEL</p>
                        </span>

                        <span
                            id={styles[inprogress]}
                            className={styles.matchStatus}
                        >
                            <p> 2 </p>
                            <p className={styles.NameStatus}>SPIEL LÄUFT</p>
                        </span>

                        <span id={styles[ended]} className={styles.matchStatus}>
                            <p> 3 </p>
                            <p className={styles.NameStatus}> SPIEL BEENDET</p>
                        </span>

                        <span
                            id={styles[completed]}
                            className={styles.matchStatus}
                        >
                            <p> 4 </p>
                            <p className={styles.NameStatus}>ABGESCHLOSSEN</p>
                        </span>

                        <span
                            id={styles[official]}
                            className={styles.matchStatus}
                        >
                            <p> 5 </p>
                            <p className={styles.NameStatus}>OFFIZIELL</p>
                        </span>
                    </div>
                </div>
                <div className={styles.headerScore}>
                    <p className={styles["header-score__name"]}>
                        {this.state.homeTeam}
                    </p>
                    <div className={styles["header-score__score"]}>
                        {this.state.shootout && (
                            <div className={styles.matchShootout}>n.P.</div>
                        )}
                        <div className={styles.matchScore}>
                            <span>{this.state.homeGoals}</span>:
                            <span>{this.state.awayGoals}</span>
                        </div>
                        <div className={styles.halftimeScore}>
                            (<span>{this.state.homeHTGoals}</span>:
                            <span>{this.state.awayHTGoals}</span>)
                        </div>
                    </div>
                    <p className={styles["header-score__name"]}>
                        {this.state.awayTeam}
                    </p>
                </div>
                <div className={styles.headerLeagueName}>
                    <p>{this.state.leagueName}</p>
                    <p>{this.state.leagueType}</p>
                </div>
                <div className={styles.headerLeagueInfo}>
                    <p>
                        <Tag /> <span>Spiel {this.state.gameNum}</span>
                    </p>
                    <p>
                        <Time />{" "}
                        <span>
                            {this.state.matchDate}, {this.state.matchTime}
                        </span>
                    </p>
                    <p>
                        <Location /> <span>{this.state.matchLocation}</span>
                    </p>
                    <p className={styles.headerLeagueUmpire}>
                        <Umpire />
                        {this.state.umpires?.map((umpire) => {
                            return (
                                <span key={umpire.id}>{umpire.fullName}</span>
                            );
                        })}
                    </p>
                </div>
            </Fragment>
        );
    }
}

export default HeaderField;
